/* import __COLOCATED_TEMPLATE__ from './sender-selector.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type InboxApi from 'embercom/services/inbox-api';
import type Session from 'embercom/services/session';
import type SenderEmailAddressSummary from 'embercom/objects/inbox/sender-email-address-summary';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import { type ReplyChannel, replyChannelIsChat } from 'embercom/objects/inbox/composer-pane';
import { isEmpty } from '@ember/utils';

interface Args {
  admin: AdminWithPermissions;
  sender: SenderEmailAddressSummary | AdminWithPermissions;
  replyChannel: ReplyChannel;
  onSenderChange: (sender: any) => void;
  setCustomAddressExists: (customAddressExists: boolean) => void;
  recipientUserId?: string;
  onSenderBrandMismatch?: (hasMismatch: boolean) => void;
}

interface Signature {
  Args: Args;
}

export default class SenderSelector extends Component<Signature> {
  @service declare inboxApi: InboxApi;
  @service declare session: Session;

  @tracked senderEmailAddresses: (SenderEmailAddressSummary | AdminWithPermissions)[] = [];
  @tracked isLoading = false;
  @tracked senderEmailFilter = '';
  @tracked isBrandFiltered = false;
  @tracked hasBrandMismatch = false;

  private lastLoadedRecipientUserId?: string;

  readonly replyChannelIsChat = replyChannelIsChat;

  popover?: { hide: () => void; show: () => void };

  get isBrandEnforcementEnabled(): boolean {
    return this.session.workspace.isFeatureEnabled('outbound-email-brand-enforcement');
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.loadSenderEmailAddresses();
  }

  async loadSenderEmailAddresses() {
    let recipientUserId = this.isBrandEnforcementEnabled ? this.args.recipientUserId : undefined;
    this.lastLoadedRecipientUserId = this.args.recipientUserId;
    this.isLoading = true;
    let { addresses: customEmailAddresses, filteredByBrandId } =
      await this.inboxApi.fetchSenderEmailAddresses(recipientUserId);
    this.args.setCustomAddressExists(!!customEmailAddresses.length);
    this.isBrandFiltered = this.isBrandEnforcementEnabled && filteredByBrandId != null;

    if (this.isBrandFiltered) {
      // When brand-filtered, don't include admin's personal email
      this.senderEmailAddresses = [...customEmailAddresses];

      // Check if current sender matches the recipient's brand
      let currentSenderId = this.args.sender?.id;
      let currentSenderInList = customEmailAddresses.some((addr) => addr.id === currentSenderId);
      // Keep the original sender — just flag the mismatch so the UI warns and Send is disabled
      let mismatch = !currentSenderInList;
      this.hasBrandMismatch = mismatch;
      this.args.onSenderBrandMismatch?.(mismatch);
    } else {
      this.senderEmailAddresses = [this.args.admin, ...customEmailAddresses];
      this.hasBrandMismatch = false;
      this.args.onSenderBrandMismatch?.(false);
    }
    this.isLoading = false;
  }

  @action maybeReloadForRecipient() {
    if (this.args.recipientUserId !== this.lastLoadedRecipientUserId) {
      this.loadSenderEmailAddresses();
    }
  }

  @action setPopover(popover: { hide: () => void; show: () => void }) {
    this.popover = popover;
  }

  @action changeSender(newSender: any) {
    if (newSender.verified === false) {
      return;
    }

    this.args.onSenderChange(newSender);
    this.popover?.hide();
    this.senderEmailFilter = '';

    // Re-evaluate brand mismatch after sender change
    if (this.isBrandEnforcementEnabled && this.isBrandFiltered) {
      let isValid = this.senderEmailAddresses.some((addr) => addr.id === newSender.id);
      this.hasBrandMismatch = !isValid;
      this.args.onSenderBrandMismatch?.(!isValid);
    }
  }

  get currentReplyChannelIsChat(): boolean {
    return this.replyChannelIsChat(this.args.replyChannel);
  }

  get filteredSenderEmail() {
    if (isEmpty(this.senderEmailFilter)) {
      return this.senderEmailAddresses;
    }
    let regexp = new RegExp(this.senderEmailFilter, 'i');
    return this.senderEmailAddresses.filter(
      (sender) => (sender.email && regexp.test(sender.email)) || regexp.test(sender.name),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::NewConversation::SenderSelector': typeof SenderSelector;
    'inbox2/new-conversation/sender-selector': typeof SenderSelector;
  }
}
